import Carousel from "@/design-system/carousel";
import type { IViewportWrapperRequiredProps } from "@/src/master/ViewportInjector";
import ViewportWrapper from "@/src/master/ViewportInjector";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { Loading } from "@Components/Loading";
import { Device } from "@Components/Modules/ViewportResize";
import * as stylex from "@stylexjs/stylex";

import AlphabetList from "../common/AlphabetList";
import type { ICategoriesCarouselItem } from "../common/CategoriesCarousel";
import { CategoryLink } from "../common/links/CategoryLink";

const styles = stylex.create({
    carousel: {
        gap: "10px",
        scrollSnapType: "x mandatory",
        gridAutoColumns: "auto",
    },
    carouselContainer: {
        paddingBlock: "0",
        paddingInline: "20px",
    },
    inlineTable: {
        gap: "10px",
        display: "flex",
        flexWrap: "wrap",
    },
});

export interface IShoppingMallTabBrandsFilter extends IComponentAjax<ICategoriesCarouselItem[]> {
    selectedCategory: string;
    onFilterChange: (categoryName: string) => void;
    onAnchorClick: (text: string) => void;
    allFiltersText: string;
    groupKeys: string[];
}

interface IShoppingMallTabBrandsFilterState extends IComponentAjaxState {
    categories: string[];
}

class ShoppingMallTabBrandsFilter extends ComponentAjax<
    ICategoriesCarouselItem[],
    IShoppingMallTabBrandsFilter & IViewportWrapperRequiredProps,
    IShoppingMallTabBrandsFilterState
> {
    public static defaultProps: IShoppingMallTabBrandsFilter = {
        selectedCategory: null,
        onFilterChange: null,
        onAnchorClick: null,
        allFiltersText: null,
        groupKeys: [],
    };

    constructor(props: IShoppingMallTabBrandsFilter & IViewportWrapperRequiredProps) {
        super(props);

        this.state = {
            isLoading: this.props.initialData ? this.props.initialData.length === 0 : true,
            categories: this.getCategoriesNames(this.props.initialData),
        };

        this.anchorClick = this.anchorClick.bind(this);
    }

    public componentDidMount() {
        if (this.state.isLoading === false) {
            return;
        }

        this.ajaxRequest();
    }

    public render() {
        const renderCategories = () => {
            if (!this.state.categories || this.state.categories.length === 0) {
                return null;
            }

            if (this.props.device === Device.Mobile || this.props.device === Device.Tablet) {
                return (
                    <Carousel
                        showArrows={this.props.device !== Device.Mobile}
                        styleX={styles.carousel}
                        carouselContainerStyleX={styles.carouselContainer}
                    >
                        {this.state.categories.map(this.renderCategoryButton)}
                    </Carousel>
                );
            }

            return (
                <ul {...stylex.props(styles.inlineTable)}>
                    {this.state.categories.map((item, i) => (
                        <li key={i}>{this.renderCategoryButton(item, i)}</li>
                    ))}
                </ul>
            );
        };

        return (
            <div>
                {this.state.isLoading && <Loading />}
                {renderCategories()}
                <AlphabetList
                    groupKeys={this.props.groupKeys}
                    onAnchorClick={this.anchorClick}
                    minFullWidthDevice="Laptop"
                />
                {this.renderAjaxErrorContainer()}
            </div>
        );
    }

    protected ajaxSuccess(response: ICategoriesCarouselItem[]): void {
        if (!response) {
            this.setState({ isLoading: false });
            return;
        }

        const categories = this.getCategoriesNames(response);

        if (this.props.allFiltersText) {
            categories.splice(0, 0, this.props.allFiltersText);
        }

        this.setState({ isLoading: false, categories });
    }

    protected ajaxFail(): boolean {
        this.setState({ isLoading: false });
        return true;
    }

    private getCategoriesNames = (categoriesItems: ICategoriesCarouselItem[]): string[] => {
        if (!categoriesItems || categoriesItems.length === 0) {
            return [];
        }

        return categoriesItems.map((item: ICategoriesCarouselItem) => item.name);
    };

    private categoryClick = (category: string, pos: number): void => {
        if (pos === 0 && !this.props.selectedCategory) {
            return;
        }

        if (this.props.selectedCategory === category) {
            return;
        }

        if (this.props.onFilterChange) {
            const categoryName: string = pos === 0 ? null : category;

            this.props.onFilterChange(categoryName);
        }
    };

    private renderCategoryButton = (category: string, pos: number): JSX.Element => {
        const onClick = () => {
            this.categoryClick(category, pos);
        };

        let initialData = category === this.props.selectedCategory;
        if (!this.props.selectedCategory) {
            initialData = pos === 0;
        }

        return <CategoryLink key={category} onClick={onClick} active={initialData} text={category} />;
    };

    private anchorClick = (text: string): void => {
        if (this.props.onAnchorClick) {
            this.props.onAnchorClick(text);
        }
    };
}

const ShoppingMalltabBrandsFilterConnect = (props: IShoppingMallTabBrandsFilter) => (
    <ViewportWrapper Component={ShoppingMallTabBrandsFilter} props={props} />
);

export { ShoppingMalltabBrandsFilterConnect as ShoppingMallTabBrandsFilter };
